import type { CustomTooltipProps } from '@tremor/react';
import { formatMxn } from '../lib/format';

interface Factory {
  currentLabel: string;
  priorLabel: string;
}

/**
 * Returns a Tremor customTooltip component that shows current / prior /
 * delta for the hovered bucket. Implemented as a factory so each chart
 * tab can bind its own labels without re-keying every tooltip prop on
 * every render.
 *
 * Why we don't use Tremor's default tooltip: it lists the two series as
 * separate lines, no delta. For an at-a-glance "is this bucket up or
 * down?" we want the delta one tap-target away.
 */
export function makeSalesDeltaTooltip({ currentLabel, priorLabel }: Factory) {
  return function SalesDeltaTooltip({ payload, active, label }: CustomTooltipProps) {
    if (!active || !payload || payload.length === 0) return null;

    const cur = Number(payload.find((p) => p.dataKey === currentLabel)?.value ?? 0);
    const pri = Number(payload.find((p) => p.dataKey === priorLabel)?.value ?? 0);
    const delta = cur - pri;
    const pct = pri !== 0 ? (delta / pri) * 100 : null;

    // Colors: emerald for positive delta, red for negative, slate for flat.
    const deltaColor =
      Math.abs(delta) < pri * 0.005 ? 'text-tremor-content'
      : delta >= 0 ? 'text-emerald-700 dark:text-emerald-400'
      : 'text-red-700 dark:text-red-400';
    const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '·';

    const pctText = pct === null ? '' : ` (${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%)`;

    return (
      <div className="bg-tremor-background rounded-tremor-default shadow-lg border border-tremor-border p-2.5 text-xs min-w-[180px]">
        <div className="font-medium text-tremor-content-strong mb-1">{label}</div>
        <div className="flex justify-between gap-3 items-baseline">
          <span className="text-tremor-content">{currentLabel}</span>
          <span className="font-semibold tabular-nums">{formatMxn(cur)}</span>
        </div>
        <div className="flex justify-between gap-3 items-baseline">
          <span className="text-tremor-content">{priorLabel}</span>
          <span className="tabular-nums">{formatMxn(pri)}</span>
        </div>
        <div className="mt-1 pt-1 border-t border-tremor-border flex justify-between gap-3 items-baseline">
          <span className="text-tremor-content">Δ</span>
          <span className={`tabular-nums font-medium ${deltaColor}`}>
            {arrow} {delta >= 0 ? '+' : ''}{formatMxn(delta)}{pctText}
          </span>
        </div>
      </div>
    );
  };
}
